import React, { useState, useEffect } from "react";
import { useContent } from "../../hooks";
import { Modal } from "./Modal";

export type Address = {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    fullAddress?: string;
};

export type MapModalProps = {
    isOpen: boolean;
    onClose: () => void;
    address: Address;
    title?: string;
};

type TravelMode = "driving" | "walking" | "bicycling" | "transit";

export const MapModal: React.FC<MapModalProps> = ({ isOpen, onClose, address, title }) => {
    const { common } = useContent();
    const mapContent = common?.map;

    // `?? ""`, weil `Modal` einen `string` verlangt. `map.title` ist im Schema
    // erzwungen (`common.ts:101`) und in beiden Sprachen befuellt («Standort» /
    // «Location»); `undefined` entsteht nur, weil der `map`-Block selbst optional
    // ist. Ein leerer Titel ist sichtbar falsch, ein englischer auf der deutschen
    // Seite still falsch.
    const modalTitle = title || mapContent?.title || "";
    const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [customStartPoint, setCustomStartPoint] = useState<string>("");
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    // `undefined` zugelassen, damit die Fehlertexte ohne englischen Ersatztext
    // durchgereicht werden koennen. Sie sind im Velite-Schema erzwungen
    // (`common.ts:115-121`, kein `.optional()` innerhalb von `errors`) und in
    // beiden Sprachen befuellt — nur der optionale `map`-Block darueber macht sie
    // typseitig `undefined`. Zeile 251 rendert ohnehin nur bei truthy Wert, also
    // verhaelt sich `undefined` dort wie `null`.
    const [locationError, setLocationError] = useState<string | null | undefined>(null);
    const [travelMode, setTravelMode] = useState<TravelMode>("driving");

    // Format address for Google Maps
    const formatAddressForMaps = (addr: Address): string => {
        if (addr.fullAddress) {
            return encodeURIComponent(addr.fullAddress);
        }
        const parts: string[] = [];
        if (addr.street) {
            parts.push(addr.street);
        }
        if (addr.postalCode) {
            parts.push(addr.postalCode);
        }
        if (addr.city) {
            parts.push(addr.city);
        }
        if (addr.country) {
            parts.push(addr.country);
        }
        return encodeURIComponent(parts.join(", "));
    };

    const formattedAddress = formatAddressForMaps(address);
    const displayAddress =
        address.fullAddress ||
        [address.street, address.postalCode, address.city, address.country].filter(Boolean).join(", ");

    // Get user's current location
    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            setLocationError(mapContent?.errors?.notSupported);
            return;
        }

        setIsLoadingLocation(true);
        setLocationError(null);

        navigator.geolocation.getCurrentPosition(
            position => {
                setUserLocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                });
                setIsLoadingLocation(false);
            },
            error => {
                setIsLoadingLocation(false);
                const errors = mapContent?.errors;
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        setLocationError(errors?.permissionDenied);
                        break;
                    case error.POSITION_UNAVAILABLE:
                        setLocationError(errors?.positionUnavailable);
                        break;
                    case error.TIMEOUT:
                        setLocationError(errors?.timeout);
                        break;
                    default:
                        setLocationError(errors?.unknown);
                        break;
                }
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
            }
        );
    };

    // Get directions URL
    const getDirectionsUrl = (): string => {
        const origin = customStartPoint.trim() || (userLocation ? `${userLocation.lat},${userLocation.lng}` : "");
        const destination = formattedAddress;

        if (!origin) {
            // If no origin, just open Google Maps with the destination
            return `https://www.google.com/maps/search/?api=1&query=${destination}`;
        }

        return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${destination}&travelmode=${travelMode}`;
    };

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setUserLocation(null);
            setCustomStartPoint("");
            setLocationError(null);
            setIsLoadingLocation(false);
        }
    }, [isOpen]);

    // Build Google Maps embed URL
    const mapEmbedUrl = `https://www.google.com/maps?q=${formattedAddress}&output=embed`;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={modalTitle}>
            <div className="space-y-6">
                {/* Address Display */}
                <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                        <svg
                            className="w-5 h-5 mt-0.5 flex-shrink-0 text-primary-dark"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fillRule="evenodd"
                                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                clipRule="evenodd"
                            />
                        </svg>
                        <div>
                            <p className="font-semibold text-primary mb-1">{mapContent?.destination}</p>
                            <p className="text-black">{displayAddress}</p>
                        </div>
                    </div>
                </div>

                {/* Map Embed */}
                <div className="w-full" style={{ height: "400px", minHeight: "300px" }}>
                    <iframe
                        src={mapEmbedUrl}
                        width="100%"
                        height="100%"
                        style={{ border: 0, borderRadius: "8px" }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Google Maps"
                    />
                </div>

                {/* Routing Controls */}
                <div className="space-y-4 border-t border-gray-200 pt-6">
                    <h3 className="font-semibold text-primary text-lg">{mapContent?.getDirections}</h3>

                    {/* Travel Mode Selection */}
                    <div className="flex flex-wrap gap-2">
                        {(["driving", "walking", "bicycling", "transit"] as TravelMode[]).map(mode => {
                            // `?? ""` statt englischer Ersatztexte: Die vier Labels sind
                            // im Velite-Schema erzwungen (`common.ts:110-114`) und in
                            // beiden Sprachen befuellt — nur der optionale `map`-Block
                            // darueber macht sie typseitig `undefined`. Frueher standen
                            // hier «Driving», «Walking», «Bicycling», «Transit», die auf
                            // der DEUTSCHEN Seite erschienen, ohne dass etwas rot wurde.
                            const modeLabels: Record<TravelMode, string> = {
                                driving: mapContent?.travelModes?.driving ?? "",
                                walking: mapContent?.travelModes?.walking ?? "",
                                bicycling: mapContent?.travelModes?.bicycling ?? "",
                                transit: mapContent?.travelModes?.transit ?? "",
                            };
                            return (
                                <button
                                    key={mode}
                                    type="button"
                                    onClick={() => setTravelMode(mode)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        travelMode === mode
                                            ? "bg-primary-dark text-white"
                                            : "bg-gray-100 text-black hover:bg-gray-200"
                                    }`}
                                >
                                    {modeLabels[mode]}
                                </button>
                            );
                        })}
                    </div>

                    {/* Starting Point Options */}
                    <div className="space-y-3">
                        {/* Use Current Location Button */}
                        <div>
                            <button
                                type="button"
                                onClick={getCurrentLocation}
                                disabled={isLoadingLocation}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-dark text-white rounded-lg font-medium hover:bg-primary-x-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoadingLocation ? (
                                    <>
                                        <svg
                                            className="animate-spin h-5 w-5"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            />
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            />
                                        </svg>
                                        <span>{mapContent?.gettingLocation}</span>
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                            />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                        </svg>
                                        <span>{mapContent?.useCurrentLocation}</span>
                                    </>
                                )}
                            </button>
                            {locationError && <p className="mt-2 text-sm text-red-600">{locationError}</p>}
                            {userLocation && (
                                <p className="mt-2 text-sm text-green-600">
                                    ✓ {mapContent?.locationFound}: {userLocation.lat.toFixed(4)},{" "}
                                    {userLocation.lng.toFixed(4)}
                                </p>
                            )}
                        </div>

                        {/* Custom Starting Point Input */}
                        <div>
                            <label htmlFor="start-point" className="block text-sm font-medium text-primary mb-2">
                                {mapContent?.enterStartPoint}
                            </label>
                            <input
                                id="start-point"
                                type="text"
                                value={customStartPoint}
                                onChange={e => setCustomStartPoint(e.target.value)}
                                placeholder={mapContent?.startPointPlaceholder}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-dark focus:border-transparent outline-none"
                            />
                        </div>

                        {/* Get Directions Button */}
                        <button
                            type="button"
                            onClick={() => {
                                const url = getDirectionsUrl();
                                window.open(url, "_blank", "noopener,noreferrer");
                            }}
                            className="w-full px-6 py-3 bg-secondary-dark text-white rounded-lg font-semibold hover:bg-secondary-x-dark transition-colors flex items-center justify-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                                />
                            </svg>
                            <span>{mapContent?.getDirectionsButton}</span>
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default MapModal;
